import { MyFeedbackDetail, MyFeedbackList, type MyFeedbackListQuery } from '@myrewards/contracts';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { request } from './client';

// Settings › My preferences › My feedback (APP-138 §D1, §D8): the person's own sent feedback,
// one piece with its conversation, and their reply to the MyRewards team.

const root = '/api/v1/me/feedback';
const one = (displayId: string): string => `${root}/${encodeURIComponent(displayId)}`;

/** Open (Received, In review, Planned), Closed (Done, Not planned), or All. */
export type MyFeedbackView = MyFeedbackListQuery['view'];

/** The view an address asks for (`?view=closed`); Open when it names none. */
export function myFeedbackView(params: URLSearchParams): MyFeedbackView {
  const view = params.get('view');
  return view === 'closed' || view === 'all' ? view : 'open';
}

export const myFeedbackKeys = {
  all: ['my-feedback'] as const,
  lists: ['my-feedback', 'list'] as const,
  list: (view: MyFeedbackView, page: number, size: number) =>
    ['my-feedback', 'list', view, page, size] as const,
  one: (displayId: string) => ['my-feedback', 'one', displayId] as const,
};

export function myFeedbackQuery(view: MyFeedbackView, paging: { page: number; size: number }) {
  return queryOptions({
    queryKey: myFeedbackKeys.list(view, paging.page, paging.size),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({
        view,
        page: String(paging.page),
        size: String(paging.size),
      });
      return request(`${root}?${params.toString()}`, { schema: MyFeedbackList, signal });
    },
    staleTime: 15_000,
  });
}

/**
 * One piece, with its conversation. Reading it marks the team's replies as seen, so it is read
 * afresh each time the page opens (the route's loader), and not again as the page mounts.
 */
export function myFeedbackItemQuery(displayId: string) {
  return queryOptions({
    queryKey: myFeedbackKeys.one(displayId),
    queryFn: ({ signal }) => request(one(displayId), { schema: MyFeedbackDetail, signal }),
    staleTime: 0,
    refetchOnMount: false,
  });
}

const ReplySent = z.object({ entryId: z.uuid() }).strict();

/** The person's reply in MyRewards. The page then reads the piece, the list and `me` again. */
export function sendMyReply(displayId: string, body: string) {
  return request(`${one(displayId)}/replies`, {
    method: 'POST',
    body: { body },
    schema: ReplySent,
  });
}
